import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { alertLabel, CHANNEL_LABEL } from '../lib/alertLabels';
import { Card, cx, Empty, ErrorText, Input, PageHeader, Pill, Select } from '../components/ui';

/** Delivery log (INT-ALR-05): what reached people, on which channel, and why something didn't. */

interface Row {
  id: string;
  channel: string;
  status: string;
  attempts: number;
  lastError: string | null;
  heldForDigest: boolean;
  createdAt: string;
  sentAt: string | null;
  eventKey: string;
  title: string;
  link: string | null;
  critical: boolean;
  user: { id: string; name: string; email: string };
}
interface Resp {
  summary: { channel: string; status: string; count: number }[];
  items: Row[];
}

const STATUS_TONE: Record<string, 'up' | 'warn' | 'down' | 'neutral'> = { SENT: 'up', DELIVERED: 'up', READ: 'up', QUEUED: 'neutral', SKIPPED: 'warn', FAILED: 'down' };

export function Deliveries() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [status, setStatus] = useState('FAILED,SKIPPED');
  const [channel, setChannel] = useState('');
  const [q, setQ] = useState('');
  const qs = new URLSearchParams({ status, ...(channel ? { channel } : {}), ...(q.trim() ? { q: q.trim() } : {}) }).toString();
  const { data, error } = useQuery({ queryKey: ['deliveries', qs], queryFn: () => api<Resp>(`/admin/deliveries?${qs}`), refetchInterval: 30_000 });

  const channels = ['WEB_PUSH', 'TELEGRAM', 'EMAIL', 'IN_APP'];
  const count = (ch: string, st: string[]) => (data?.summary ?? []).filter((s) => s.channel === ch && st.includes(s.status)).reduce((a, s) => a + s.count, 0);

  return (
    <>
      <PageHeader title="Delivery log" sub="Alerts by channel over the last 7 days, and the reason when something didn't go out" />
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {channels.map((ch) => (
          <Card key={ch} className="flex flex-col gap-1 p-3.5">
            <span className="text-xs text-muted">{CHANNEL_LABEL[ch]}</span>
            <span className="font-mono text-lg">{count(ch, ['SENT', 'DELIVERED', 'READ'])} <span className="text-xs text-muted">sent</span></span>
            <span className="text-xs">
              <span className={cx(count(ch, ['SKIPPED']) ? 'text-warn' : 'text-muted')}>{count(ch, ['SKIPPED'])} skipped</span>
              {' · '}
              <span className={cx(count(ch, ['FAILED']) ? 'text-down' : 'text-muted')}>{count(ch, ['FAILED'])} failed</span>
            </span>
          </Card>
        ))}
      </section>

      <Card>
        <div className="flex flex-wrap items-end gap-2.5 border-b border-line p-3.5">
          <label className="flex flex-col gap-1 text-xs text-muted">
            Status
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="py-1.5">
              <option value="FAILED,SKIPPED">Problems (failed or skipped)</option>
              <option value="FAILED">Failed</option>
              <option value="SKIPPED">Skipped</option>
              <option value="QUEUED">Waiting (incl. digests)</option>
              <option value="SENT,DELIVERED,READ">Sent</option>
              <option value="">Everything</option>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Channel
            <Select value={channel} onChange={(e) => setChannel(e.target.value)} className="py-1.5">
              <option value="">All channels</option>
              {channels.map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
            </Select>
          </label>
          <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-xs text-muted">
            Search person or alert
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or alert" className="py-1.5" />
          </label>
        </div>
        <ErrorText error={error ? errorText(error) : null} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                {['When', 'Person', 'Alert', 'Channel', 'Status', 'Reason'].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data?.items.map((r) => (
                <tr key={r.id} className="border-t border-[#1a1f27] align-top">
                  <td className="px-4 py-2.5 whitespace-nowrap text-sub">{fmtTime(r.createdAt, tz, true)}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-semibold">{r.user.name}</span>
                    <br />
                    <span className="text-xs text-muted">{r.user.email}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.link ? <Link to={r.link} className="text-ink">{r.title}</Link> : r.title}
                    <br />
                    <span className="text-xs text-muted">{alertLabel(r.eventKey).label}{r.critical ? ' · critical' : ''}</span>
                  </td>
                  <td className="px-4 py-2.5">{CHANNEL_LABEL[r.channel] ?? r.channel}{r.heldForDigest && <span className="text-xs text-muted"> · digest</span>}</td>
                  <td className="px-4 py-2.5"><Pill tone={STATUS_TONE[r.status] ?? 'neutral'}>{r.status.toLowerCase()}</Pill></td>
                  <td className="px-4 py-2.5 text-xs text-sub">{r.lastError ?? (r.attempts > 1 ? `${r.attempts} attempts` : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.items.length && <Empty>Nothing here. {status.includes('FAILED') ? 'No failed or skipped deliveries.' : ''}</Empty>}
        </div>
      </Card>
    </>
  );
}
